import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import { execApi } from '../services/api';

// The standalone assistant panel — the hub's front door. One general assistant that
// (a) answers WITH data (the trusted primitives) and (b) GUIDES/routes from the
// dashboard map (what exists, where to look, what a metric means). Role-aware: the
// backend only reveals/answers panels the user may see. See memory: exec-ask-freetext-pilot.

interface AskDetail { label: string; value: string; }
interface AskAnswer {
  question: string;
  ok: boolean;
  primitive: string;
  interpretation: string;
  headline: string;
  details: AskDetail[];
  note: string | null;
  route: string | null;
  params: Record<string, unknown>;
}
interface Panel {
  id: string; name: string; route: string; purpose: string;
  examples: string[];
}

export default function ExecAskPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState<AskAnswer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);

  // Suggested questions are seeded from the dashboard map (one example per panel) so
  // they always reflect what THIS user can actually ask; fall back to a static seed.
  useEffect(() => {
    execApi.getDashboardMap()
      .then((res) => {
        const panels = (res.data as Panel[]) || [];
        setSuggestions(panels.map((p) => p.examples?.[0]).filter(Boolean).slice(0, 6));
      })
      .catch(() => setSuggestions([
        'כמה מטופלים מחזיק כל פסיכיאטר החודש?',
        'מה זמן ההמתנה לאינטייק פסיכיאטר במכבי?',
        'מה זה ניצולת ואיפה רואים אותה?',
      ]));
  }, []);

  const ask = async (question: string) => {
    const text = question.trim();
    if (text.length < 2) return;
    setLoading(true);
    setError(null);
    setAnswer(null);
    try {
      const res = await execApi.ask(text);
      setAnswer(res.data as AskAnswer);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'שגיאה בשליחת השאלה');
    } finally {
      setLoading(false);
    }
  };

  return (
    // dir stays OFF this flex row: it would flip the sidebar's `order-last` and throw
    // the nav to the left, unlike every other page. The content keeps its own dir.
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden" dir="rtl">
        <Header title="דייויד · האנליסט הדיגיטלי" />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="mx-auto max-w-3xl space-y-5">
            {/* Hero input — David's card */}
            <div className="rounded-2xl bg-white p-6 shadow-sm border">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-800 text-xl shadow-sm">🧠</div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-800">דייויד</h1>
                  <p className="text-sm text-gray-500">האנליסט הדיגיטלי של טליאז — שאלו אותי על הנתונים או על הדשבורד, ואכוון ללוח הנכון.</p>
                </div>
              </div>
              <textarea
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) ask(q); }}
                placeholder="לדוגמה: כמה מטופלי מכבי מחכים לאינטייק פסיכיאטר?"
                aria-label="שאלה לדייויד"
                className="mt-4 w-full resize-none rounded-xl border border-gray-300 p-3 text-sm focus:border-indigo-500 focus:outline-none"
                rows={3}
              />
              <div className="mt-2 flex items-center justify-between">
                <span className="text-xs text-gray-500">Ctrl/⌘ + Enter לשליחה</span>
                <button
                  onClick={() => ask(q)}
                  disabled={loading || q.trim().length < 2}
                  className="rounded-lg bg-indigo-600 px-5 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
                >
                  {loading ? 'דייויד חושב…' : 'שאל את דייויד'}
                </button>
              </div>

              {!answer && !loading && suggestions.length > 0 && (
                <div className="mt-4">
                  <div className="text-xs font-medium text-gray-500">שאלות לדוגמה</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {suggestions.map((ex) => (
                      <button
                        key={ex}
                        onClick={() => { setQ(ex); ask(ex); }}
                        className="rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs text-indigo-700 hover:bg-indigo-100"
                      >
                        {ex}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Result */}
            {error && <div className="rounded-xl bg-red-50 p-4 text-sm text-red-700">{error}</div>}

            {answer && (
              <div className="space-y-4">
                {answer.interpretation && (
                  <div className="rounded-xl bg-white p-4 shadow-sm border">
                    <div className="text-xs font-medium text-gray-500">🧠 איך דייויד הבין את השאלה</div>
                    <div className="mt-1 text-sm text-gray-700">{answer.interpretation}</div>
                  </div>
                )}

                <div className={`rounded-xl p-5 shadow-sm border ${answer.ok ? 'bg-indigo-50 border-indigo-100' : 'bg-amber-50 border-amber-100'}`}>
                  <div className="text-base font-semibold text-gray-800">{answer.headline}</div>
                  {answer.route && (
                    <button
                      onClick={() => navigate(answer.route as string)}
                      className="mt-3 inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                    >
                      פתח את הלוח ←
                    </button>
                  )}
                </div>

                {answer.details.length > 0 && (
                  <div className="rounded-xl border bg-white divide-y shadow-sm">
                    {answer.details.map((d, i) => (
                      <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm">
                        <span className="text-gray-600">{d.label}</span>
                        <span className="font-medium text-gray-900">{d.value}</span>
                      </div>
                    ))}
                  </div>
                )}

                {answer.note && <div className="px-1 text-xs text-gray-500">{answer.note}</div>}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
